// Artist Profile hook — the band-scoped Profile settings tab: genre chips (≤ 3), comparable
// artists with a validation tier, the AI/discovery prose (sound / audience / goals / what's-working / brand voice),
// and the "open to support opportunities" booking-discovery toggle (born-blank ON). Hand-written over the generated
// client via the tenant-keyed wrappers — key ['band', bandId, 'band-profile']. GET/PUT are BOTH gated on
// band:manage server-side (an impersonator without band:manage is 403'd), so the tab is band:manage-gated in
// the UI too. The save PUTs the WHOLE tab and returns the fresh canonical shape, which reseeds the cache; it NEVER
// reports success on an unpersisted write.
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError } from "./apiError.js";

type BandProfileRead = components["schemas"]["BandProfileRead"];
type UpdateBandProfileRequest = components["schemas"]["UpdateBandProfileRequest"];
type ComparableArtist = components["schemas"]["ComparableArtist"];
type ComparableArtistMatch = components["schemas"]["ComparableArtistMatch"];
type ComparableArtistSearchResponse = components["schemas"]["ComparableArtistSearchResponse"];

export type {
  BandProfileRead,
  UpdateBandProfileRequest,
  ComparableArtist,
  ComparableArtistMatch,
  ComparableArtistSearchResponse,
};

/** The UI cap on genres (≤ 3) — also a `core.bands` CHECK + a server 400. A fixed UI bound, not a business
 * value.*/
export const MAX_GENRES = 3;

/** The comparable-artist tier colours. Only LEGACY pills carry one: a pre-2026-09-06 Songstats pill is coloured
 * server-side from its stored monthly listeners, and a hand-typed pill keeps the colour its owner picked. A pill
 * picked from Spotify is "" (uncoloured) — Spotify's developer policy forbids building benchmarking metrics from
 * its data. "" is deliberately not red, because red claims a small following rather than a missing measurement. */
export const COMPARABLE_TIERS = ["green", "yellow", "red"] as const;
export type ComparableTier = (typeof COMPARABLE_TIERS)[number] | "";

/** Shortest name we will search Spotify for. A one-character name matches nothing useful, and the search is a
 * user-initiated vendor call. */
export const MIN_COMPARABLE_QUERY = 2;

/** `GET /bands/{bandId}/settings/profile` (band:manage) — the Profile tab. Born-blank when no row exists yet
 * (empty prose, no comparables, openToSupport ON). A 403 (no band:manage) surfaces as the query error. */
export function useBandProfile() {
  const { api } = useApp();
  return useBandQuery<BandProfileRead>(
    ["band-profile"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/settings/profile", {
        params: { path: { bandId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { staleTime: 30_000 },
  );
}

/**
 * Build a COMPLETE PUT body from a loaded profile, with optional overrides.
 *
 * The endpoint saves the whole tab — `UpdateBandProfileRequest` defaults every field to null and the service
 * writes them all — so a caller that sends only the field it changed silently NULLS the prose, the
 * comparables and the genres. That is a live risk now that the booking-discovery toggle lives on the Account
 * tab and writes the same row as the Artist Profile tab. Go through here and it cannot happen.
 */
export function toUpdateRequest(
  p: BandProfileRead,
  overrides: Partial<UpdateBandProfileRequest> = {},
): UpdateBandProfileRequest {
  return {
    genres: p.genres ?? [],
    comparableArtists: p.comparableArtists ?? [],
    soundDescription: p.soundDescription ?? null,
    targetAudience: p.targetAudience ?? null,
    goals: p.goals ?? null,
    whatsWorking: p.whatsWorking ?? null,
    brandVoice: p.brandVoice ?? null,
    thingsToAvoid: p.thingsToAvoid ?? null,
    openToSupport: p.openToSupport ?? true,
    ...overrides,
  };
}

/** `PUT /bands/{bandId}/settings/profile` (band:manage) → BandProfileRead — save the whole tab. A 400 (> 3 genres
 * / malformed comparable) surfaces verbatim; the tab keeps its draft on error. Reseeds the cache on success. */
export function useSaveBandProfile() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();

  return useBandMutation<BandProfileRead, UpdateBandProfileRequest>(
    async (band, body) => {
      const { data, error, response } = await api.PUT("/api/v1/bands/{bandId}/settings/profile", {
        params: { path: { bandId: band } },
        body,
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    {
      onSuccess: (data) => qc.setQueryData<BandProfileRead>(["band", bandId, "band-profile"], data),
    },
  );
}

/** `GET /bands/{bandId}/settings/profile/comparables/search?q=` (band:manage) — Spotify catalogue candidates for a
 * typed name, for the band to pick from.
 *
 * A mutation rather than a query because it fires once, imperatively, when someone presses Enter — there is no
 * ambient "current search" to keep fresh.
 *
 * **It always resolves — read the response, do not catch it.** `providerConfigured: false` means this host holds
 * no Spotify app credentials, so nothing can be searched; an empty `items` with `providerConfigured: true` means
 * no matches. Those send a user to fix two different things, so the caller renders them differently. A rejection
 * is a transport failure and nothing else. */
export function useSearchComparables() {
  const { api } = useApp();

  return useBandMutation<ComparableArtistSearchResponse, string>(async (bandId, q) => {
    const { data, error, response } = await api.GET(
      "/api/v1/bands/{bandId}/settings/profile/comparables/search",
      { params: { path: { bandId }, query: { q } } },
    );
    if (!response.ok || !data) throw new ApiError(response.status, error);
    return data;
  });
}
